// Import necessary dependencies
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnInit } from '@angular/core';
import { BehaviorSubject, firstValueFrom, map, Observable, forkJoin, tap, catchError, finalize, throwError, of, switchMap } from 'rxjs';
import { environment } from '../../../environments/environment';
import * as _ from 'lodash';

const API_V = 'v1';

export interface Scheme {
  name: string;
  code: string;
  schemeAdministratorCode: string;
  managedCareCompanyCode: string;
  captitionScheme: string;
  discontinuedDate: string;
  cmsReferenceNumber: string;
  category: string;
  schemeTelephoneNumber: string;
  schemeEmail: string;
  schemeUrl: string;
}

export interface Plan {
  code: string;
  schemeCode: string;
  option: string;
  remarks: string;
  discontinuedDate: string;
  startDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface PlanOption {
  code: string;
  planCode: string;
  option: string;
  remarks: string;
  balanceBilling: boolean;
  discontinuedDate: string;
  outHospitalReimbursementRate: string;
  pmbReimbursementRate: string;
  inHospitalReimbursementRate: string;
  insuranceCode: string;
  capitationOption: string;
}
export interface Scheme {
  name: string;
  code: string;
  schemeAdministratorCode: string;
  managedCareCompanyCode: string;
  captitionScheme: string;
  discontinuedDate: string;
  cmsReferenceNumber: string;
  category: string;
  schemeTelephoneNumber: string;
  schemeEmail: string;
  schemeUrl: string;
}

interface SchemesSearchResponse {
  schemes: {
    hasPreviousPage: boolean;
    hasNextPage: boolean;
    pageIndex: number;
    pageSize: number;
    totalPages: number;
    totalCount: number;
    thisCount: number;
    pageResult: Scheme[];
  };
}

export interface Discipline {
  code: string;
  description: string;
  tariffMedicalTypeCode: string;
}

interface DisciplineSearchResponse {
  tariffDisciplines: {
    hasPreviousPage: boolean;
    hasNextPage: boolean;
    pageIndex: number;
    pageSize: number;
    totalPages: number;
    totalCount: number;
    thisCount: number;
    pageResult: Discipline[];
  };
}

interface PlanOptionResponse<T> {
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  pageIndex: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  thisCount: number;
  pageResult: T[];
}

export interface TariffCode {
  code: string;
  description: string;
  disciplineCode: string | null;
  tariffMedicalTypeCode: string;
  typeCode: string;
}

export interface PriceGroup {
  code: string;
  description: string;
  tariffMedicalTypeCode: string;
  tariffDisciplineCode: string;
  planOptionCode: string;
}

interface PriceGroupResponse<T> {
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  pageIndex: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  thisCount: number;
  pageResult: T[];
}

export interface MsrRow {
  tariffRandSchemeFixed: any;
  tariffRandRcfSchemeRate: any;
  tariffCodeCode: string;
  description: string;
  planOptionCode: string;
  priceGroupType: string;
  priceGroupCode: string;
  tariffRandCalculated: number;
  numberOfUnits: number;
  unitTypeDescription: string;
  tariffRatePublished: number;
  ruleType: string;
  extraInfo: string;
}

export interface SpaRow {
  tariffRandSchemeFixed: any;
  tariffRandRcfSchemeRate: any;
  tariffCodeCode: string;
  description: string;
  planOptionCode: string;
  priceGroupType: string;
  priceGroupCode: string;
  tariffRandCalculated: number;
  numberOfUnits: number;
  unitTypeDescription: string;
  tariffRatePublished: number;
  ruleType: string;
  extraInfo: string;
}

export interface NetRow {
  tariffRandSchemeFixed: any;
  tariffRandRcfSchemeRate: any;
  tariffCodeCode: string;
  description: string;
  planOptionCode: string;
  priceGroupType: string;
  priceGroupCode: string;
  tariffRandCalculated: number;
  numberOfUnits: number;
  unitTypeDescription: string;
  tariffRatePublished: number;
  ruleType: string;
  extraInfo: string;
}


interface Paged<T> {
  pageResult: T[];
}

export interface ProductBase {
  nappiCode: string;
  name: string;
  description: string;
  singleExitPrice: number;
  standardPacksize: number;
  dispensingVolumePacksize: number;
  drugSchedule: number;
  product0201: number;
  manufacturer: {
    code: string;
    description: string;
  };
  genericIndicatorCode: string;
  nonSubstitutableProductFlag: boolean;
  drugExclusions: any[];
  genderFlag: string;
  maximumAdultDosage: number;
  activeSubstances: Array<{
    code: string;
    description: string;
  }>;
}

export interface Medicine extends ProductBase {
  type: 'medicine';
  strengthMetric1: number;
  unitOfMeasure1: string;
  presentation: string;
  atcLevel5Code: string;
}

export interface Material extends ProductBase {
  type: 'material';
  catalogueNumber: string;
  productSize: string;
  rxUnit: string;
}

export interface InsuredBenefit {
  tariffRandCalculatedIH: number;
  tariffRandRcfSchemeRateIH: number;
  tariffRandSchemeFixedIH: number;
  tariffRandCalculatedOH: number;
  tariffRandRcfSchemeRateOH: number;
  tariffRandSchemeFixedOH: number;
  ihPercentage: number;
  ohPercentage: number;
  beneficiary: string;
  tariffCode: {
    code: string;
    description: string;
    disciplineCode: string;
    tariffMedicalTypeCode: string;
    typeCode: string;
  };
  tariffCodeCode: string;
  tariffDiscipline: {
    description: string;
    code: string;
    tariffMedicalTypeCode: string;
  };
  tariffDisciplineCode: string;
  planOptionCode: string;
  tariffRandCalculated: number;
  tariffRandRcfSchemeRate: number;
  tariffRandSchemeFixed: number;
  numberOfUnits: number;
  description: string;
  priceGroupType: string;
  priceGroupCode: string;
  ruleType: string;
  extraInfo: string;
  unitTypeDescription: string;
  unitTypeCode: string;
  typeCode: string;
  tariffRatePublished: number;
  tariffRcfPublished: number;
  tariffModifiers: any[];
}

interface InsuredBenefitsResponse {
  insuredBenefits: {
    hasPreviousPage: boolean;
    hasNextPage: boolean;
    pageIndex: number;
    pageSize: number;
    totalPages: number;
    totalCount: number;
    thisCount: number;
    pageResult: InsuredBenefit[];
  };
  isDemoData: boolean;
  errors: any[];
  info: any[];
  success: boolean;
}

interface AuthResponse {
  accessToken: string;
}

@Injectable({
  providedIn: 'root'
})
export class MedpraxService {
  private apiUrl = 'api/accounting';
  public baseTariffUrl = 'https://tariffs.api.medprax.co.za';
  private baseUrl = 'https://schemes.api.medprax.co.za';
  private baseProductsUrl = 'https://products.api.medprax.co.za';
  private readonly BASE_URL_PROD = 'https://auth.api.medprax.co.za';
  private readonly apiVersion = 'v1';

  private tariffProcessingStatusSubject = new BehaviorSubject<any>(null);
  private nappiProcessingStatusSubject = new BehaviorSubject<any>(null);

  tariffStatus$ = this.tariffProcessingStatusSubject.asObservable();
  nappiStatus$ = this.nappiProcessingStatusSubject.asObservable();
  private discountSubject = new BehaviorSubject<{ value: number, type: 'percentage' | 'fixed' }>({ value: 0, type: 'percentage' });
  discount$ = this.discountSubject.asObservable();
  private tokens: { [key: string]: { accessToken: string } } = {};
  private pendingAuthRequests: { [key: string]: Observable<any> } = {};

  constructor(private http: HttpClient) {

  }

  /** Helper to build headers with bearer token for a given API type */
  public headersFor(apiType: 'SCHEME' | 'PRODUCT' | 'TARIFF', year?: string | number): Observable<{ [key: string]: string }> {
    // Default to current year if not provided
    const yearValue = year ? year.toString() : new Date().getFullYear().toString();
    const headers: { [key: string]: string } = { 'Year': yearValue };
    const token = this.tokens[apiType]?.accessToken;

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      return of(headers);
    }

    // If no token exists, authenticate first
    return this.refreshAuth(apiType).pipe(
      map(() => {
        const newToken = this.tokens[apiType]?.accessToken;
        if (!newToken) {
          throw new Error(`Failed to get token for ${apiType} API`);
        }
        headers['Authorization'] = `Bearer ${newToken}`;
        return headers;
      })
    );
  }

  // Helper method to handle 401 errors with retry
  private handle401Error<T>(apiType: 'SCHEME' | 'PRODUCT' | 'TARIFF', request: Observable<T>): Observable<T> {
    return request.pipe(
      catchError(error => {
        if (error.status === 401) {
          // Try to refresh auth and retry the request
          return this.refreshAuth(apiType).pipe(
            switchMap(() => request)
          );
        }
        return throwError(() => error);
      })
    );
  }

  public getToken(apiType: 'SCHEME' | 'PRODUCT' | 'TARIFF'): string | null {
    return this.tokens[apiType]?.accessToken || null;
  }

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('token');
    return new HttpHeaders().set('Authorization', `Bearer ${token}`);
  }

  // Fetch all tariff rates for a specific group
  getTariffRatesByGroup(groupId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/tariff-rates/group/${groupId}`, {
      headers: this.getAuthHeaders(),
      responseType: 'json' // Ensure JSON response
    });
  }

  resetDefaultRate(groupId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/reset-default-rate`, { groupId });
  }

  getDefaultRate(groupId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/default-rate/${groupId}`);
  }

  updateDiscount(value: number, type: 'percentage' | 'fixed' = 'percentage') {
    this.discountSubject.next({ value, type });
  }

  // 1) Disciplines
  searchDisciplines(
    medType: string,
    startsWith: string = ''
  ): Observable<Discipline[]> {
    const url = `${this.baseTariffUrl}/${this.apiUrl}/${this.apiVersion}/disciplines/${medType}/search/1/100`;
    const body = {
      filters: [
        {
          propertyName: 'Code',
          operation: 'StartsWith',
          value: startsWith
        }
      ],
      filterJoin: 'And',
      ascendingOrder: true,
      sortKey: 'code'
    };
    const apiType = 'TARIFF';

    return this.headersFor(apiType).pipe(
      switchMap(headers =>
        this.handle401Error(apiType,
          this.http.post<DisciplineSearchResponse>(url, body, { headers })
        )
      ),
      map(resp => resp.tariffDisciplines.pageResult)
    );
  }

  // 2) Schemes
  searchSchemes(startsWith: string = ''): Observable<Scheme[]> {
    const url = `${this.baseUrl}/api/${this.apiVersion}/schemes/search/1/100`;
    const body = {
      filters: [
        {
          propertyName: 'Code',
          operation: 'StartsWith',
          value: startsWith
        }
      ],
      filterJoin: 'And',
      ascendingOrder: true,
      sortKey: 'Code'
    };
    const apiType = 'SCHEME';

    return this.headersFor(apiType).pipe(
      switchMap(headers =>
        this.handle401Error(apiType,
          this.http.post<SchemesSearchResponse>(url, body, { headers })
        )
      ),
      map(resp => resp.schemes.pageResult)
    );
  }

  // 3) PlanOptions for a scheme
  getPlanOptions(schemeCode: string): Observable<PlanOption[]> {
    const url = `${this.baseUrl}/api/${this.apiVersion}/schemes/${schemeCode}/list/planoptions`;
    const apiType = 'SCHEME';

    return this.headersFor(apiType).pipe(
      switchMap(headers =>
        this.handle401Error(apiType,
          this.http.get<{ planOptions: PlanOptionResponse<PlanOption> }>(url, { headers })
        )
      ),
      map(resp => resp.planOptions.pageResult)
    );
  }

  /** Fetch Plan Options (Fixed API Call) */
  getLocalPlanOptions(planCode: string): Observable<any> {
    return this.http.get<any>(`${environment.API_URL}/api/scheme/plan/options/${planCode}`, { headers: this.getAuthHeaders() }).pipe(
      map(response => response.planOptions || []),
      catchError(error => {
        console.error("Failed to fetch plan options:", error.message);
        return of([]);
      })
    );
  }

  // 4) TariffCode autocomplete
  searchTariffCodes(term: string): Observable<TariffCode[]> {
    const body = {
      filters: [
        { propertyName: 'Code', operation: 'Contains', value: term }
      ],
      filterJoin: 'And',
      ascendingOrder: true,
      sortKey: 'code'
    };
    const url = `${this.baseTariffUrl}/api/${this.apiVersion}/tariffcodes/medical/search/1/100`;
    const apiType = 'TARIFF';

    return this.headersFor(apiType).pipe(
      switchMap(headers =>
        this.handle401Error(apiType,
          this.http.post<{
            tariffCodes: {
              pageResult: TariffCode[];
            }
          }>(url, body, { headers })
        )
      ),
      map(res => res.tariffCodes.pageResult)
    );
  }

  // 5) PriceGroups toggles
  listPriceGroups(
    medType: string,
    disciplineCode: string,
    isContract = true,
    model = true
  ): Observable<PriceGroup[]> {
    const body = {
      disciplineCode,
      isContract,
      model
    };
    const apiType = 'TARIFF';

    return this.headersFor(apiType).pipe(
      switchMap(headers =>
        this.handle401Error(apiType,
          this.http.post<{ tariffPriceGroups: PriceGroupResponse<PriceGroup> }>(
            `${this.baseTariffUrl}/api/${this.apiVersion}/pricegroups/${medType}/list`,
            body,
            { headers }
          )
        )
      ),
      map(res => {
        const filtered = res.tariffPriceGroups.pageResult?.filter(pg => pg.description?.toLowerCase().indexOf('not in use') === -1);
        return _.orderBy(filtered, ['description'], ['asc']);
      }),
      catchError(error => {
        console.error('Error fetching price groups:', error);
        return throwError(() => error);
      })
    );
  }

  // 6) MSR table
  listMsr(
    medType: string,
    payload: {
      tariffCodes: string[],
      planOptionCode: string,
      disciplineCode: string,
      model: boolean,
      ascendingOrder: boolean,
      sortKey: string
    },
    year?: string | number
  ): Observable<MsrRow[]> {
    const apiType = 'TARIFF';

    return this.headersFor(apiType, year).pipe(
      switchMap(headers =>
        this.handle401Error(apiType,
          this.http.post<{ msrs: Paged<MsrRow> }>(
            `${this.baseTariffUrl}/api/${this.apiVersion}/msr/${medType}/list`,
            payload,
            { headers }
          )
        )
      ),
      map(res => res.msrs.pageResult)
    );
  }

  // 7) SPA table
  listSpa(
    medType: string,
    payload: {
      tariffCodes: string[],
      planOptionCode: string,
      disciplineCode: string,
      model: boolean,
      ascendingOrder: boolean,
      sortKey: string
    },
    year?: string | number
  ): Observable<SpaRow[]> {
    const apiType = 'TARIFF';

    return this.headersFor(apiType, year).pipe(
      switchMap(headers =>
        this.handle401Error(apiType,
          this.http.post<{ contracts: Paged<SpaRow> }>(
            `${this.baseTariffUrl}/api/${this.apiVersion}/contracts/${medType}/list`,
            payload,
            { headers }
          )
        )
      ),
      map(res => res.contracts.pageResult)
    );
  }

  // 8) Network table
  listNetwork(
    medType: string,
    payload: {
      tariffCodes: string[],
      planOptionCode: string,
      disciplineCode: string,
      model: boolean,
      ascendingOrder: boolean,
      sortKey: string
    },
    year?: string | number
  ): Observable<NetRow[]> {
    const apiType = 'TARIFF';

    return this.headersFor(apiType, year).pipe(
      switchMap(headers =>
        this.handle401Error(apiType,
          this.http.post<{ networks: Paged<NetRow> }>(
            `${this.baseTariffUrl}/api/${this.apiVersion}/networks/${medType}/list`,
            payload,
            { headers }
          )
        )
      ),
      map(res => res.networks.pageResult)
    );
  }

  /** Fetch Schemes (Fixed API Call) */
  getSchemesList(search: string): Observable<any> {

    return this.http.post<any>(`${environment.API_URL}/api/medprax/schemes/by-name`, { schemeName: search }, { headers: this.getAuthHeaders() }).pipe(
      map(response => response.schemeList || []),
      catchError(error => {
        console.error("Failed to fetch schemes:", error.message);
        return of([]);
      })
    );
  }

  getSchemeDetails(schemeCode: string): Observable<any> {
    return this.http.get<any>(`${environment.API_URL}/api/scheme/plan/${schemeCode}`, { headers: this.getAuthHeaders() }).pipe(
      tap(response => console.log('RAW API RESPONSE:', response)), // Debug log
      map(response => {
        if (!response || !response.plans) {
          throw new Error('Invalid API response: plans not found');
        }
        return response;
      }),
      catchError(error => {
        console.error("Failed to fetch scheme details:", error.message);
        return of({ plans: [] }); // Return an empty array if there's an error
      })
    );
  }

  /** Fetch Plan Options (Fixed API Call) */
  /*   getPlanOptions(planCode: string): Observable<any> {
      return this.http.get<any>(`${environment.API_URL}/api/scheme/plan/options/${planCode}`, { headers: this.getAuthHeaders() }).pipe(
        map(response => response.planOptions || []),
        catchError(error => {
          console.error("Failed to fetch plan options:", error.message);
          return of([]);
        })
      );
    } */

  getProductPrice(searchTerm: string, type: 'medicine' | 'material', year?: string | number): Observable<any> {
    if (!searchTerm || searchTerm.length < 2) {
      return of([]);
    }

    const apiType = 'PRODUCT';
    const url = `${this.baseProductsUrl}/api/${this.apiVersion}/${type === 'medicine' ? 'medicines' : 'materials'}/search/1/100`;
    const body = {
      filters: [
        {
          propertyName: "name",
          operation: "Contains",
          value: searchTerm
        }
      ],
      filterJoin: "And",
      ascendingOrder: true,
      sortKey: type === 'medicine' ? "nappiCode" : "name"
    };

    return this.headersFor(apiType, year).pipe(
      switchMap(headers =>
        this.handle401Error(apiType,
          this.http.post<any>(url, body, { headers })
        )
      ),
      map(response => {
        if (!response || !response.products || !response.products.pageResult) {
          console.warn(`No ${type} found for search term:`, searchTerm);
          return [];
        }
        return response.products.pageResult.map((item: any) => ({
          ...item,
          type: type === 'medicine' ? 'medication' : 'material'
        }));
      })
    );
  }

  getNappiPrice(nappiCodes: string[], year?: string | number): Observable<any> {
    return this.getProductPrice(nappiCodes[0], 'medicine', year);
  }

  getMaterialPrice(nappiCodes: string[], year?: string | number): Observable<any> {
    return this.getProductPrice(nappiCodes[0], 'material', year);
  }

  searchProducts(term: string, year?: string | number): Observable<any[]> {
    return forkJoin({
      medicines: this.getProductPrice(term, 'medicine', year),
      materials: this.getProductPrice(term, 'material', year)
    }).pipe(
      map(({ medicines, materials }) => [
        ...medicines,
        ...materials
      ])
    );
  }

  listInsuredBenefits(
    medType: string,
    payload: {
      tariffCodes: string[],
      planOptionCode: string,
      disciplineCode: string,
      model: boolean
    },
    year?: string | number
  ): Observable<InsuredBenefit[]> {
    const apiType = 'TARIFF';
    const url = `${this.baseTariffUrl}/api/${this.apiVersion}/insuredbenefits/${medType}/list`;

    return this.headersFor(apiType, year).pipe(
      switchMap(headers =>
        this.handle401Error(apiType,
          this.http.post<InsuredBenefitsResponse>(url, payload, { headers })
        )
      ),
      map(response => {
        if (!response.success) {
          throw new Error('Failed to fetch insured benefits');
        }
        return response.insuredBenefits.pageResult;
      })
    );
  }

  refreshAuth(apiType?: 'SCHEME' | 'PRODUCT' | 'TARIFF'): Observable<any> {

    // If a specific API type is requested, only refresh that one
    if (apiType) {
      // If there's already a pending request for this API type, return it
      if (this.pendingAuthRequests[apiType]) {
        return this.pendingAuthRequests[apiType];
      }

      // Create new auth request for this API type
      const authRequest = this.http.post<any>(
        `${this.BASE_URL_PROD}/api/${this.apiVersion}/Authenticate/login/email`,
        {
          UniqueReferenceHost: `MEDPRAX.${apiType}.API`,
        },
        {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          })
        }
      ).pipe(
        tap(response => {
          if (!response.token) {
            throw new Error('No token received from auth service');
          }
          this.tokens[apiType] = { accessToken: response.token };
        }),
        catchError(error => {
          console.error(`❌ Tariff API: Error authenticating ${apiType} API:`, error);
          delete this.pendingAuthRequests[apiType];
          return throwError(() => error);
        }),
        finalize(() => {
          // Clear the pending request after completion
          delete this.pendingAuthRequests[apiType];
        })
      );

      // Store the pending request
      this.pendingAuthRequests[apiType] = authRequest;
      return authRequest;
    }

    // If no specific API type is requested, refresh all
    const authRequests = ['SCHEME', 'PRODUCT', 'TARIFF'].map(type =>
      this.refreshAuth(type as 'SCHEME' | 'PRODUCT' | 'TARIFF')
    );

    return forkJoin(authRequests).pipe(
      map(() => {
        return true;
      }),
      catchError(error => {
        console.error('❌ Tariff API: Error refreshing all auth tokens:', error);
        return throwError(() => error);
      })
    );
  }
}
